import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Pencil, Trash2 } from "lucide-react";

import LinkPlus from "@/assets/icons/authentication/LinkPlus.svg";
import VideoUploaderWithPreview from "@/components/ui/VideoUploaderWithPreview";
import VideoPlayerV3, { DEFAULT_BLURHASH } from "@/components/ui/VideoPlayerV3";
import type { FileDetails } from "@/types/types";

import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import { updateUser } from "@/redux/feature/user/userSlice";

type PortfolioVideoSourceType = "upload" | "link";
type PortfolioVideoItem = {
  id: string;
  brandName: string;
  isPersonalProject: boolean;
  description: string;
  sourceType: PortfolioVideoSourceType;
  linkUrl?: string;
  file?: FileDetails;
  uploading?: boolean;
  uploadError?: string | null;
  createdAt: string;
};

const ProfilePortfolioVideosSection = () => {
  const dispatch = useAppDispatch();

  const portfolioVideos: PortfolioVideoItem[] =
    useAppSelector((s) => (s.user.user as any)?.portfolioVideos) ?? [];

  const setPortfolioVideos = (next: PortfolioVideoItem[]) => {
    dispatch(updateUser({ field: "portfolioVideos", value: next }));
  };

  // uploader external state (single)
  const [uploadedFiles, setUploadedFiles] = useState<FileDetails[]>([]);

  // draft link + meta
  const [draftLink, setDraftLink] = useState("");
  const [draftBrand, setDraftBrand] = useState("");
  const [draftPersonal, setDraftPersonal] = useState(false);
  const [draftDesc, setDraftDesc] = useState("");

  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // for list playback
  const [playingUrl, setPlayingUrl] = useState("");

  const uploaded = uploadedFiles[0] || null;
  const uploadReady = !!uploaded?.live_url;

  const hasUpload = uploadedFiles.length > 0;
  const hasLink = draftLink.trim().length > 0;
  const effectiveMode: PortfolioVideoSourceType = hasUpload
    ? "upload"
    : hasLink
      ? "link"
      : "upload";

  const canSubmit = useMemo(() => {
    if (!draftDesc.trim()) return false;
    if (hasUpload) return uploadReady;
    if (hasLink) {
      try {
        new URL(draftLink.trim());
        return true;
      } catch {
        return false;
      }
    }
    return false;
  }, [uploadReady, hasUpload, hasLink, draftLink, draftDesc]);

  const resetDraft = () => {
    setUploadedFiles([]);
    setDraftLink("");
    setDraftBrand("");
    setDraftPersonal(false);
    setDraftDesc("");
    setEditingId(null);
    setBusy(false);
  };

  const startEdit = (item: PortfolioVideoItem) => {
    setEditingId(item.id);
    setDraftBrand(item.brandName ?? "");
    setDraftPersonal(!!item.isPersonalProject);
    setDraftDesc(item.description ?? "");

    if (item.sourceType === "link") {
      setDraftLink(item.linkUrl ?? "");
      setUploadedFiles([]);
    } else {
      setDraftLink("");
      setUploadedFiles(item.file ? [item.file as FileDetails] : []);
    }
  };

  const handleDelete = (id: string) => {
    setPortfolioVideos(portfolioVideos.filter((v) => v.id !== id));
    if (editingId === id) resetDraft();
  };

  const handleAddOrSave = async () => {
    if (busy) return;

    if (!draftDesc.trim()) return toast.error("Please add a description.");

    const mode = effectiveMode;

    if (mode === "upload" && !uploadReady) {
      return toast.error("Please wait until the upload finishes.");
    }

    if (mode === "link") {
      if (!draftLink.trim()) return toast.error("Please add a video link.");
      try {
        new URL(draftLink.trim());
      } catch {
        return toast.error("Please provide a valid link URL.");
      }
    }

    if (!hasUpload && !hasLink) {
      return toast.error("Please upload a video or paste a link.");
    }

    setBusy(true);

    try {
      const basePatch = {
        brandName: draftBrand.trim(),
        isPersonalProject: draftPersonal,
        description: draftDesc.trim(),
      };

      if (editingId) {
        const next: PortfolioVideoItem[] = portfolioVideos.map(
          (v): PortfolioVideoItem => {
            if (v.id !== editingId) return v;

            if (mode === "upload") {
              return {
                ...v,
                ...basePatch,
                sourceType: "upload",
                linkUrl: undefined,
                file: uploaded as any,
                uploading: false,
                uploadError: null,
              };
            }

            return {
              ...v,
              ...basePatch,
              sourceType: "link",
              linkUrl: draftLink.trim(),
              file: undefined,
              uploading: false,
              uploadError: null,
            };
          },
        );

        setPortfolioVideos(next);
        toast.success("Updated!");
        resetDraft();
        return;
      }

      const id = crypto.randomUUID();

      const newItem: PortfolioVideoItem =
        mode === "upload"
          ? ({
              id,
              ...basePatch,
              sourceType: "upload",
              file: uploaded as any,
              uploading: false,
              uploadError: null,
              createdAt: new Date().toISOString(),
            } as PortfolioVideoItem)
          : ({
              id,
              ...basePatch,
              sourceType: "link",
              linkUrl: draftLink.trim(),
              uploading: false,
              uploadError: null,
              createdAt: new Date().toISOString(),
            } as PortfolioVideoItem);

      setPortfolioVideos([...portfolioVideos, newItem]);
      toast.success("Added!");
      resetDraft();
    } catch (e) {
      console.error(e);
      toast.error("Failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full space-y-3">
      <div>
        <h6 className="text-[#121217] text-sm font-medium">
          Upload your Portfolio videos
        </h6>
        <p className="text-[#475467] text-[10px] mt-[2px]">
          UGC, brand ads, reels, or speaking samples work best.
        </p>
      </div>

      <div className="bg-[#FAFAFA] rounded-[20px] p-3 shadow-sm">
        <div className="flex flex-wrap justify-center gap-3">
          <div className="w-[150px] shrink-0">
            <div className="rounded-[14px] border border-dashed border-[#D0D5DD] bg-[#FAFAFA] px-2 py-2.5 flex flex-col items-center gap-2">
              <VideoUploaderWithPreview
                maxFiles={1}
                useExternalState
                externalFiles={uploadedFiles}
                setExternalFiles={setUploadedFiles}
                showSelectedFiles
                showUploadAnimationOnly={false}
                useLiveUrl={false}
                height="120px"
                width="120px"
                cardDesignSize="h-[120px] w-[120px]"
                accept={{
                  "video/mp4": [],
                  "video/webm": [],
                  "video/ogg": [],
                  "video/quicktime": [],
                }}
                onFilesUploaded={() => {}}
              />

              {!uploadReady && uploadedFiles.length > 0 && (
                <p className="text-[9px] text-[#98A2B3]">
                  Uploading… please wait
                </p>
              )}

              <div className="flex items-center gap-2 w-full">
                <div className="flex-1 h-px bg-[#E4E7EC]" />
                <span className="text-[9px] text-[#98A2B3] font-medium">
                  or
                </span>
                <div className="flex-1 h-px bg-[#E4E7EC]" />
              </div>

              <div className="relative w-full">
                <input
                  type="text"
                  placeholder="Add link"
                  value={draftLink}
                  onChange={(e) => {
                    setDraftLink(e.target.value);
                    if (e.target.value && uploadedFiles.length > 0) {
                      setUploadedFiles([]);
                    }
                  }}
                  className="w-full text-[9px] placeholder:text-[#98A2B3] outline-none
                    bg-white border border-[#EEEEEE] rounded-lg
                    pl-2 pr-[38px] py-1.5"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (!draftLink.trim()) return;
                    try {
                      new URL(draftLink.trim());
                      toast.success("Link looks good");
                    } catch {
                      toast.error("Invalid link URL");
                    }
                  }}
                  className="absolute right-1 top-1/2 -translate-y-1/2
                    px-1.5 py-[2px] rounded-[8px]
                    border border-[#FDE2D1] bg-[#FFF7ED]
                    text-[#F97316] text-[8px] font-medium
                    hover:bg-[#FFEDD5] flex items-center gap-x-1"
                >
                  <LinkPlus />
                  <span>add</span>
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2 rounded-lg border border-[#EDEDED] pr-2.5 bg-white">
              <input
                type="text"
                placeholder="Brand name"
                value={draftBrand}
                onChange={(e) => setDraftBrand(e.target.value)}
                className="flex-1 h-8 px-2.5 text-[10px] placeholder:text-[#98A2B3] outline-none rounded-lg"
              />
              <label className="flex items-center gap-1 text-[10px] text-[#121217] font-medium cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={draftPersonal}
                  onChange={(e) => setDraftPersonal(e.target.checked)}
                  className="w-3 h-3 accent-[#F97316] border-[#D0D5DD]"
                />
                Personal
              </label>
            </div>

            <textarea
              placeholder="What was this video for and what was your role?"
              rows={5}
              value={draftDesc}
              onChange={(e) => setDraftDesc(e.target.value)}
              className="w-full px-2.5 py-2 rounded-lg border border-[#EDEDED]
                text-[10px] placeholder:text-[#959FB2]
                outline-none bg-white resize-none"
            />

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleAddOrSave}
                disabled={busy || !canSubmit}
                className="py-2 px-3 bg-[#FF6600] disabled:opacity-50 text-white text-[10px] font-medium rounded-full"
              >
                {editingId
                  ? "Save Changes"
                  : busy
                    ? "Saving..."
                    : "Add to Profile"}
              </button>

              {editingId && (
                <button
                  type="button"
                  onClick={resetDraft}
                  className="py-2 px-3 border border-[#EDEDED] bg-white text-[#121217] text-[10px] font-medium rounded-full"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {portfolioVideos.length > 0 && (
        <p className="text-[10px] text-[#475467]">
          Added videos: {portfolioVideos.length}
        </p>
      )}

      <div className="space-y-2">
        {portfolioVideos.map((v) => {
          const url =
            v.sourceType === "upload"
              ? (v.file?.live_url as string | undefined)
              : (v.linkUrl as string | undefined);

          const thumb =
            v.sourceType === "upload"
              ? ((v.file as any)?.thumbnail as string)
              : "";
          const hash =
            v.sourceType === "upload"
              ? ((v.file as any)?.hash as string) || DEFAULT_BLURHASH
              : DEFAULT_BLURHASH;

          return (
            <div
              key={v.id}
              className="bg-white border border-[#EAECF0] rounded-[16px] p-2 flex gap-3 shadow-sm"
            >
              <div className="w-[96px] h-[140px] rounded-xl overflow-hidden border border-[#EEE] bg-[#F8F8F8]">
                {v.sourceType === "upload" && url ? (
                  <VideoPlayerV3
                    url={url}
                    thumbnailUrl={thumb}
                    playing={playingUrl === url}
                    muted={false}
                    blurhash={hash}
                    onPlay={() =>
                      setPlayingUrl((cur) => (cur === url ? "" : url))
                    }
                    border="rounded-xl"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] text-[#98A2B3]">
                    No video
                  </div>
                )}
              </div>

              <div className="flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h6 className="text-[12px] font-medium text-[#121217] leading-tight">
                      {v.brandName || (v.isPersonalProject ? "Personal" : "—")}
                    </h6>
                    <p className="text-[10px] text-[#98A2B3] mt-0.5">
                      {v.sourceType === "upload"
                        ? "Uploaded video"
                        : "Video link"}
                    </p>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => startEdit(v)}
                      className="p-1.5 rounded-lg border border-[#EEE] bg-white hover:bg-[#FAFAFA]"
                      aria-label="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(v.id)}
                      className="p-1.5 rounded-lg border border-[#FDE2D1] bg-[#FFF7ED] text-[#F97316] hover:bg-[#FFEDD5]"
                      aria-label="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <p className="text-[10px] text-[#475467] mt-2 line-clamp-3">
                  {v.description}
                </p>

                <div className="mt-2 text-[9px] text-[#98A2B3] break-all">
                  {v.sourceType === "link"
                    ? v.linkUrl
                    : v.file?.live_url || "Not uploaded yet"}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={resetDraft}
        className="text-xs text-[#FF6600] font-medium"
      >
        + Add More Videos
      </button>
    </div>
  );
};

export default ProfilePortfolioVideosSection;
